import PlatformUtil from "./PlatformUtil";
import CommonParameter from "../parameters/CommonParameter";
import MetaParameter from "../parameters/MetaParameter";
import ConsoleLog from "./ConsoleLog";
import DataParameter from "../parameters/DataParameter";
import ExtraParameter from "../parameters/ExtraParameter";
import ParameterStore from "../parameters/ParameterStore";
import AttrDataUtil from "./AttrDataUtil";
import CommonWebParameter from "../parameters/CommonWebParameter";

const TAG = 'RequestUtil';

export default {
  /**
   * Merging the implicit log schema data with parameter data and commonData.
   * @param schema {ImplicitLogSchemaType}
   * @param params {RequestParams}
   * @param commonData {CommonDataType}
   * @return {RequestParams}
   */
  generateImplicitLogRequestParams(schema: ImplicitLogSchemaType, params: RequestParams, commonData: CommonDataType): RequestParams {
    const newParams = { ...params };

    newParams.meta = Object.assign({}, schema.meta, params.meta);
    newParams.data = Object.assign({}, schema.data, params.data);

    newParams.data.domain = params.data?.domain || commonData.domain;
    newParams.data.pageName = params.data?.pageName || commonData.pageName;
    newParams.data.pvid = commonData.pvid || new CommonWebParameter().getJSON().web.pvid;

    const serverTime = params.data?.serverTime || commonData.serverTime;
    if (serverTime) {
      newParams.data.serverTime = serverTime;
    }

    return newParams;
  },

  /**
   * Generating RequestParams object with additional common data and webview specific data.
   * @param params {RequestParams}
   * @param commonParameter {CommonParameter}
   * @param campaignParams {CampaignParamType}
   */
  generateRequestJSON(params: RequestParams, commonParameter: CommonParameter, campaignParams: CampaignParamType): RequestParams {
    const { meta, data, extra } = params;

    const metaJSON = meta ? this.generateMetaParameter(meta).getJSON() : { meta : {} };
    const dataJSON = data ? this.generateDataParameter(data).getJSON() : { data : {} };
    const extraJSON = this.generateExtraParameter(extra || {}).getJSON();

    commonParameter.setEventTime(new Date().toISOString());
    commonParameter.setMemberSrl();
    const commonJSON = commonParameter.getJSON();

    if (PlatformUtil.isApp()) {
      extraJSON['$webview'] = commonParameter.getExtraWebviewJSON(true);
    }

    return {
      ...commonJSON,
      ...metaJSON,
      ...dataJSON,
      ...extraJSON,
      ...campaignParams,
    }
  },

  /**
   * Generate a JSON object to send the meta log data.
   *
   * @param meta {MetaParamsType} JSON object for data
   * @returns {MetaParameter} Returns generated MetaParameter
   */
  generateMetaParameter(meta: MetaParamsType): MetaParameter {
    const {
      schemaId,
      schemaVersion
    } = meta;

    if (!schemaId || !schemaVersion) {
      ConsoleLog.w(TAG, `#generateMetaParameter - schemaId: ${schemaId}, schemaVersion: ${schemaVersion}`);
    }
    return new MetaParameter().setSchemaId(schemaId).setSchemaVersion(schemaVersion);
  },

  /**
   * Generate a JSON object to send the log data.
   *
   * @param data {object} JSON object for data
   * @returns {DataParameter} Returns generated DataParameter
   */
  generateDataParameter(data: object): DataParameter {
    return new DataParameter().setData(data);
  },

  /**
   * Generate a JSON object to send the extra log data.
   *
   * @param extra {object} JSON object for extra
   * @returns {ExtraParameter} Returns generated ExtraParameter
   */
  generateExtraParameter(extra: object): ExtraParameter {
    return new ExtraParameter().setExtraData(extra).setSentTime(new Date().toISOString());
  },

  /**
   * Generate RequestParams data object and setting common fields for all events.
   * @param eventType {string}
   * @param target {HTMLElement}
   * @param parameterStore {ParameterStore}
   * @param commonData {CommonDataType}
   * @return {RequestParams}
   */
  generateEventData(eventType: string, target: HTMLElement, parameterStore: ParameterStore, commonData: CommonDataType): RequestParams {
    const eventData = AttrDataUtil.getRequestParam(target);
    const params = parameterStore.replaceDynamicString(eventData);
    params.data = params.data || {};

    const componentName = AttrDataUtil.getComponentName(target);
    const widgetName = AttrDataUtil.getWidgetName(target);
    params.data.componentName = params.data.componentName || componentName;
    params.data.widgetName = params.data.widgetName || widgetName;
    params.data.eventName = params.data.eventName || `${eventType}_${params.data.componentName || params.data.widgetName}`;
    params.data.traceId = AttrDataUtil.getTraceId(target) || commonData.traceId;
    params.data.serverTime = parseInt(AttrDataUtil.getServerTime(target), 10) || commonData.serverTime;

    return params;
  },

  /**
   * Merge two RequestParams data sets. We only merge data, extra, and meta objects.
   * @param attrData: data from data-web-log-param 
   * @param customData: data passed as function param
   * @returns merged data
   */
  mergeRequestParams(attrData: RequestParams = {}, customData: RequestParams = {}): RequestParams {
    const newParams: RequestParams = {};
    newParams.data = Object.assign({}, attrData?.data, customData?.data);
    newParams.extra = Object.assign({}, attrData?.extra, customData?.extra);
    newParams.meta = Object.assign({}, attrData?.meta, customData?.meta);

    return newParams;
  }
}
